// WHAT: Form component for managing database URL with validation and debugging
// WHY:  Provides interface for setting Supabase database URL with connection testing
// DOMAIN: Settings → Supabase
// DEPENDENCIES: setDatabaseUrl logic, core.invoke
// RISKS:
//   - NOTE: URL validation may not catch all invalid formats
//   - NOTE: Debug functionality may expose sensitive information
//   - NOTE: Store status check may not reflect actual connection state

import { core } from "@tauri-apps/api";
import { Database, CheckCircle, AlertCircle, Loader2, Bug, Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Button, Input, Label, Stack, Badge } from "@/components/ui";

import { setDatabaseUrl } from "../logic/saveSupabaseToStore";

/**
 * WHAT:
 *   Form component that provides interface for managing database URL with
 *   validation, debugging, and connection status checking.
 *
 * WHY:
 *   Enables users to configure Supabase database connection with tools for
 *   troubleshooting connection issues.
 *
 * BEHAVIOR:
 *   - Loads current database URL on mount
 *   - Allows editing URL
 *   - Validates and saves URL
 *   - Provides debug information
 *   - Shows connection status
 *   - Supports URL visibility toggle
 *
 * RISKS:
 *   - NOTE: URL validation may not catch all invalid formats
 *   - NOTE: Debug functionality may expose sensitive information
 *   - NOTE: Store status check may not reflect actual connection state
 *
 * @returns {JSX.Element} Database URL form component
 *
 */
export function DatabaseUrlForm() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"error" | "idle" | "success">("idle");
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [storeStatus, setStoreStatus] = useState<null | {
    status?: string;
    [key: string]: unknown;
  }>(null);
  const [saving, setSaving] = useState(false);
  const [debugging, setDebugging] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  // Load database URL when component mounts
  useEffect(() => {
    const loadDatabaseUrl = async () => {
      try {
        setLoading(true);
        const loadedUrl = await core.invoke<string>("get_database_url_cmd");
        if (loadedUrl) {
          setUrl(loadedUrl);
        }
      } catch {
        // URL might not be set yet, that's okay
        // NOTE: Database URL not set yet - this is expected on first run
      } finally {
        setLoading(false);
      }
    };

    void loadDatabaseUrl();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatus("idle");
    setDebugInfo("Saving URL...");

    try {
      await setDatabaseUrl(url);
      setStatus("success");
      setDebugInfo("URL saved successfully! ✅");
      // Reload to ensure UI is in sync
      const loadedUrl = await core.invoke<string>("get_database_url_cmd");
      if (loadedUrl) {
        setUrl(loadedUrl);
      }
    } catch (error) {
      setStatus("error");
      const errorMessage = error instanceof Error ? error.message : String(error);
      setDebugInfo(`Error: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDebugStore = async () => {
    setDebugging(true);
    try {
      const status = await core.invoke("debug_store_status");
      setStoreStatus(status as null | { [key: string]: unknown; status?: string });
      setDebugInfo("Store status retrieved ✅");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setDebugInfo(`Debug error: ${errorMessage}`);
    } finally {
      setDebugging(false);
    }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-500/10 rounded-lg">
            <Database className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Database URL</h3>
            <p className="text-sm text-slate-400">
              Configure direct PostgreSQL database connection
            </p>
          </div>
        </div>

        {/* Form */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            <span className="ml-2 text-slate-400">Loading database URL...</span>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
          >
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center space-x-2" htmlFor="database-url">
                <Database className="w-4 h-4" />
                <span>Database URL</span>
              </Label>
              <div className="relative">
                <Input
                  className="bg-slate-700 border-slate-600 text-white focus:border-orange-500 focus:ring-orange-500/20 pr-10"
                  id="database-url"
                  onChange={(e) => {
                    setUrl(e.target.value);
                  }}
                  placeholder="postgresql://postgres:password@host:5432/postgres"
                  required
                  type={showUrl ? "text" : "password"}
                  value={url}
                />
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-300"
                  onClick={() => {
                    setShowUrl(!showUrl);
                  }}
                  type="button"
                >
                  {showUrl ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Direct PostgreSQL connection string (for advanced users)
              </p>
            </div>

            {/* Action Buttons */}
            <Stack className="flex-wrap" direction="row" gap={2}>
              <Button disabled={saving || !url} size="md" type="submit" variant="primary">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save URL
                  </>
                )}
              </Button>

              <Button
                disabled={debugging}
                onClick={() => {
                  void handleDebugStore();
                }}
                size="md"
                type="button"
                variant="ghost"
              >
                {debugging ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Debugging...
                  </>
                ) : (
                  <>
                    <Bug className="w-4 h-4 mr-2" />
                    Debug Store
                  </>
                )}
              </Button>
            </Stack>
          </form>
        )}

        {/* Status Messages */}
        {status === "success" && (
          <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-400 text-sm font-medium">
              Database URL saved successfully!
            </span>
          </div>
        )}

        {status === "error" && (
          <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-400 text-sm font-medium">
              Error saving URL. Please check your connection string.
            </span>
          </div>
        )}

        {/* Debug Information */}
        {debugInfo ? (
          <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
            <div className="flex items-center space-x-2 mb-2">
              <Bug className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-300">Debug Information</span>
            </div>
            <p className="text-sm text-slate-400">{debugInfo}</p>
          </Card>
        ) : null}

        {/* Store Status */}
        {storeStatus ? (
          <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
            <div className="flex items-center space-x-2 mb-3">
              <Database className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-300">Store Status</span>
              <Badge size="sm" style="subtle" variant="default">
                Debug
              </Badge>
            </div>
            <pre className="text-xs text-slate-400 bg-slate-800/50 p-3 rounded-lg overflow-x-auto">
              {JSON.stringify(storeStatus, null, 2)}
            </pre>
          </Card>
        ) : null}

        {/* Warning Card */}
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-yellow-400 mb-1">Advanced Configuration</h4>
              <p className="text-xs text-yellow-300">
                This is for direct database access. Make sure you have the correct permissions and
                that your database allows external connections.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
